import { makeAutoObservable, action, runInAction, observable} from "mobx";
import {
  addToBasket,
  checkBasketItems,
  deleteAllBasketItems,
  deleteBaseBasketOneItem,
  forceDeleteAllBasketItems,
  updateCountItemsBasket,
  getAndMergeBasketItems,
  mergeBasketItemsNoUser,
  transferPreorderToBasket,
} from "../http/basketApi";
import { getCategories } from "../http/catalogApi";
import { createNewOrder, payOrderSertificate, createOrdinaryOrder, recoverOrdinaryOrder } from "../http/orderApi";
import { getSizeCommerceView, isBasketLineAvailable } from "../utils/onecStockAvailability";
import PreorderCartStore from './PreorderCartStore';
import {getMindboxDeviceUUID} from '../utils/favoriteUtils';
import {
  BASKET_RECOVERY_ERROR, BASKET_SNAPSHOT_ERROR, validateBasketSession,
  normalizeBasketSnapshot, readBasketRecovery, serializeBasketIdentities, basketIdentityItems,
  selectedBasketPurchaseLines, canIncreaseBasketLine, nextBasketExpiry,
} from "../utils/siteBasketSnapshot";

const PRICING_ERROR = 'Не удалось рассчитать сумму. Повторите расчёт.';
const pricingFail = () => {throw new Error(PRICING_ERROR);};
const pricingRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const pricingId = value => Number.isSafeInteger(value) && value > 0 && value <= 2147483647;
const pricingRevision = value => typeof value === 'string' && /^(0|[1-9]\d{0,18})$/.test(value) &&
  (value.length < 19 || value <= '9223372036854775807');
const pricingText = (value, cap = 255) => typeof value === 'string' && value.trim().length > 0 && value.length <= cap && !/[\u0000-\u001f\u007f]/.test(value);
const ordinaryDeviceUUID = value => typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
const ordinaryTelephone = raw => {
  if (typeof raw !== 'string' || raw.length > 100) pricingFail();
  const literal = raw.trim();
  if (!/^\+?[\d\s().-]+$/.test(literal)) pricingFail();
  let digits = literal.replace(/\D/g, '');
  // Domestic trunk8 is only a bare-number convention. An explicit+81 (etc.)
  // already supplies its country code and must never be rewritten to+7.
  if (!literal.startsWith('+') && digits.length === 11 && digits.startsWith('8')) digits = '7' + digits.slice(1);
  const value = '+' + digits;
  if (!/^\+[1-9]\d{7,14}$/.test(value)) pricingFail();
  return value;
};
const pricingKeys = (value, keys) => pricingRecord(value) && Object.keys(value).length === keys.length && keys.every(key => Object.hasOwn(value, key));
const pricingMinor = value => {
  const minor = Math.round(value * 100);
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || !Number.isSafeInteger(minor) || Math.abs(minor / 100 - value) > 1e-9) pricingFail();
  return minor;
};
// HTTP price data never becomes a basket/SKU grant. Copy own DATA only, then
// validate the frozen server contract without invoking accessors or toJSON.
const copyPricing = (value, depth = 0) => {
  if (depth > 12) pricingFail();
  if (value === null || ['string', 'boolean', 'number'].includes(typeof value)) return value;
  if (!value || typeof value !== 'object') pricingFail();
  const descriptors = Object.getOwnPropertyDescriptors(value), keys = Reflect.ownKeys(descriptors);
  if (Array.isArray(value)) {
    if (value.length > 2000 || keys.length !== value.length + 1 || keys.some(key => key !== 'length' && !/^(0|[1-9]\d*)$/.test(key))) pricingFail();
    return Object.freeze(Array.from({length: value.length}, (_, n) => {
      const field = descriptors[n]; if (!field || !Object.hasOwn(field, 'value') || !field.enumerable) pricingFail();
      return copyPricing(field.value, depth + 1);
    }));
  }
  if (![Object.prototype, null].includes(Object.getPrototypeOf(value)) || keys.length > 32) pricingFail();
  const result = {};
  for (const key of keys) {
    const field = descriptors[key];
    if (typeof key !== 'string' || ['__proto__', 'constructor', 'prototype'].includes(key) || !field.enumerable || !Object.hasOwn(field, 'value')) pricingFail();
    result[key] = copyPricing(field.value, depth + 1);
  }
  return Object.freeze(result);
};
const normalizePricing = (raw, selected, scope = 'authenticated') => {
  const value = copyPricing(raw);
  if (!pricingKeys(value, ['schemaVersion', 'scope', 'basketId', 'basketRevision', 'selectionState', 'deliveryState', 'totalPrice',
    'merchandisePrice', 'priceNoSell', 'deliveryPrice', 'promoDiscountSumm', 'promocode', 'loyalty', 'promotions', 'lines']) ||
    value.schemaVersion !== 'site-basket-pricing-v1' || value.scope !== scope || !Array.isArray(value.lines) ||
    value.lines.length !== selected.length || !['selected', 'empty'].includes(value.selectionState) ||
    value.selectionState !== (selected.length ? 'selected' : 'empty') || !['selected', 'not_selected'].includes(value.deliveryState) ||
    (scope === 'guest' ? value.basketId !== null || value.basketRevision !== null :
      value.basketId === null ? selected.length > 0 || value.basketRevision !== null : !pricingId(value.basketId) || !pricingRevision(value.basketRevision))) pricingFail();
  const money = key => pricingMinor(value[key]);
  let baseTotal = 0, lineTotal = 0; const keys = new Set();
  for (const row of value.lines) {
    const own = selected.find(line => line.lineKey === row.lineKey);
    if (!pricingKeys(row, ['lineKey', 'count', 'basePrice', 'unitPrice', 'lineTotal', 'discount']) || !own || keys.has(row.lineKey) ||
      !pricingId(row.count) || row.count !== own.count) pricingFail();
    keys.add(row.lineKey); const base = pricingMinor(row.basePrice) * row.count, total = pricingMinor(row.lineTotal);
    pricingMinor(row.unitPrice);
    if (!Number.isSafeInteger(base) || base - total !== pricingMinor(row.discount)) pricingFail();
    baseTotal += base; lineTotal += total;
  }
  if (!Number.isSafeInteger(baseTotal) || !Number.isSafeInteger(lineTotal) || baseTotal !== money('priceNoSell') || lineTotal !== money('merchandisePrice') ||
    lineTotal + money('deliveryPrice') !== money('totalPrice') || !Number.isSafeInteger(lineTotal + money('deliveryPrice')) ||
    money('promoDiscountSumm') > baseTotal - lineTotal || value.deliveryState === 'not_selected' && value.deliveryPrice !== 0) pricingFail();
  const promo = value.promocode;
  if (!pricingRecord(promo) || Object.keys(promo).some(key => !['status', 'value', 'error'].includes(key)) ||
    (Object.hasOwn(promo, 'status') && typeof promo.status !== 'boolean') || (Object.hasOwn(promo, 'value') && !pricingText(promo.value)) ||
    (Object.hasOwn(promo, 'error') && !pricingText(promo.error, 2048)) || (promo.status === true && !pricingText(promo.value))) pricingFail();
  if (!Array.isArray(value.promotions) || value.promotions.some(text => !pricingText(text))) pricingFail();
  if (value.loyalty !== null) {
    const loyalty = value.loyalty, info = loyalty.totalBonusPointsInfo, changes = loyalty.bonusPointsChanges;
    if (!pricingKeys(loyalty, ['totalBonusPointsInfo', 'bonusPointsChanges']) || !pricingKeys(info, ['status', 'availableAmountForCurrentOrder', 'balance']) ||
      info.status !== 'Success' || !pricingKeys(info.balance, ['available'])) pricingFail();
    pricingMinor(info.availableAmountForCurrentOrder); pricingMinor(info.balance.available);
    if (changes !== null) {if (!pricingKeys(changes, ['earnedAmount', 'spentAmount'])) pricingFail(); pricingMinor(changes.earnedAmount); pricingMinor(changes.spentAmount);}
  }
  return value;
};

export default class BasketStore {
  totalPrice = 0;
  priceNoSell = 0;
  delPrice = 0;
  promoNitificator = {
    color: "red",
    message: "",
  };
  certificates = [""];

  promocodeItem = null;
  promoDiscountSumm = 0;
  isLoyaltyActive = false;
  bonusesOrderEarned = 0;
  bonusesOrderAvail = 0;
  bonusesTotal = 0;
  bonusesConfirmed = 0;
  bonusesSpendAll = false;
  usedPromotions = [];
  update = true;

  _session = null;
  _sessionStarted = false;
  sessionEpoch = 0;
  localSnapshotVersion = 0;
  snapshotMode = 'unknown';
  snapshotPending = false;
  snapshotError = null;
  recoverableIdentities = [];
  recoveryProvenance = 'none';
  recoveryError = null;
  _snapshotRequestSeq = 0;
  _snapshotSettledSeq = 0;
  _snapshotClock = 0;
  _snapshotExpiryTimer = null;
  _guestRecoveryConsumed = false;
  _sessionQueue = null;
  _sessionPixelListeners = new Set();
  _enforcedMutationRequest = null;
  _preorderReconcilePromise = null;
  pricingState = 'idle';
  pricingError = null;
  pricingRetryVersion = 0;
  _pricingReceipt = null;
  _pricingContext = null;
  _ordinaryQuote = null;
  _ordinaryIntent = null;
  _ordinaryNeedsRecovery = false;
  ordinaryCheckoutState = 'idle';
  ordinaryCheckoutError = null;
  _ordinaryOrderReceipt = null;
  _ordinaryReceiptContext = null;
  _cartSection = 'ordinary';


  constructor(diliveryStore, userStore) {
    this.delivery = diliveryStore;
    this.user = userStore;
    this.preorderCart = new PreorderCartStore();
    this.init();
    makeAutoObservable(this, {preorderCart: false, _snapshotExpiryTimer: false, _sessionQueue: false, _sessionPixelListeners: false, _enforcedMutationRequest: false,
      _preorderReconcilePromise: false,
      _pricingContext: false, _pricingReceipt: observable.ref, _ordinaryQuote: observable.ref, _ordinaryIntent: observable.ref,
      _ordinaryOrderReceipt: observable.ref, _ordinaryReceiptContext: observable.ref});
  }

  beginSession(session) {
    const identity = validateBasketSession(session);
    this.preorderCart.beginSession(identity);
    this._cartSection = 'ordinary';
    this._preorderReconcilePromise = null;
    this._clearSnapshotExpiry();
    this.sessionEpoch += 1;
    this._session = identity;
    this._resetSessionQueue();
    this._sessionStarted = true;
    this._snapshotRequestSeq = 0;
    this._snapshotSettledSeq = 0;
    this.localSnapshotVersion = 0;
    this.snapshotMode = 'unknown';
    this.snapshotPending = false;
    this.snapshotError = null;
    this._guestRecoveryConsumed = false;
    this._snapshotClock = Date.now();
    this.init();
    this.totalPrice = 0; this.priceNoSell = 0; this.delPrice = 0;
    this.certificates = ['']; this.promocodeItem = null; this.promoDiscountSumm = 0;
    this.isLoyaltyActive = false; this.bonusesConfirmed = 0;
    this.promoNitificator = {color: 'red', message: ''};
    this._loadBasketRecovery();
    this._loadOrdinaryIntent();
    return this.sessionEpoch;
  }

  ensureSession(session) {
    const identity = validateBasketSession(session);
    if (this._session?.authScope === identity.authScope && this._session?.principalId === identity.principalId) return this.sessionEpoch;
    return this.beginSession(identity);
  }

  disposeSession({preservePreorderHandoff = false} = {}) {
    if (!preservePreorderHandoff) this.preorderCart.disposeSession();
    this._cartSection = 'ordinary';
    this._preorderReconcilePromise = null;
    this._clearSnapshotExpiry();
    this.sessionEpoch += 1;
    this._session = null;
    this._resetSessionQueue();
    this.init();
    this.snapshotMode = 'unknown';
    this.snapshotPending = false;
    this.snapshotError = null;
    this.totalPrice = 0; this.priceNoSell = 0; this.delPrice = 0;
    this.localSnapshotVersion = 0;
    this.recoverableIdentities = [];
    this.recoveryProvenance = 'none';
    this.recoveryError = null;
    this.certificates = ['']; this.promocodeItem = null; this.promoDiscountSumm = 0;
    this.isLoyaltyActive = false; this.bonusesConfirmed = 0;
    this.promoNitificator = {color: 'red', message: ''};
  }

  lockSession() {
    this._sessionStarted = true;
    this.preorderCart.suspendSession();
    this.disposeSession({preservePreorderHandoff: true});
  }

  get cartSection() {return this._cartSection;}
  setCartSection(value) {
    if (!['ordinary', 'preorder'].includes(value)) return false;
    this._cartSection = value; return true;
  }

  addPreorderProduct(item, sizeName, route) {
    if (!this.ordinaryOwnerReady) return {status: 'failed', error: 'Дождитесь обновления корзины.'};
    const result = this.preorderCart.add(item, sizeName, route);
    if (result.status === 'added') {
      this.setNotificationContent({item: result.line.item, currentSize: result.line.currentSize,
        count: result.line.quantity, cartKind: 'preorder'});
      this.setActiveNotification(true);
    }
    return result;
  }

  _preorderTransferBlocked() {
    if (this.startBasketNum !== 1 || this._ordinaryIntent || this._ordinaryNeedsRecovery) return true;
    if (this._session?.authScope !== 'authenticated') return false;
    try {
      // The checkout command is saved before sending the order. Keep its
      // selection intact until recovery confirms whether it was accepted.
      return localStorage.getItem(`yw:automatic-preorder-checkout:v1:${this._session.principalId}`) !== null;
    } catch {return true;}
  }

  async reconcilePreorderStock() {
    if (this._preorderReconcilePromise) return this._preorderReconcilePromise;
    const run = (async () => {
      if (process.env.REACT_APP_AUTOMATIC_PREORDER_SITE_CHECKOUT_ENABLED !== 'true' ||
        !this.ordinaryOwnerReady || !['enforced', 'legacy'].includes(this.snapshotMode) || this.snapshotLocked || this._preorderTransferBlocked() ||
        !this.preorderCart.lines.length) return false;
      const epoch = this.preorderCart.epoch;
      if (this._session?.authScope === 'guest') this._loadBasketRecovery();
      const refreshed = await this.queueSessionRefresh();
      if (refreshed.status !== 'accepted' || refreshed.value !== true ||
        epoch !== this.preorderCart.epoch) return false;
      if (!await this.preorderCart.refresh() || epoch !== this.preorderCart.epoch || this._preorderTransferBlocked()) return false;
      let moved = false;
      for (const line of [...this.preorderCart.lines]) {
        if (line.verification !== 'available' && !line.transferKey) continue;
        moved = await this._transferAvailablePreorder(line.lineKey, epoch) || moved;
      }
      if (moved && !this.preorderCart.lines.length) this.setCartSection('ordinary');
      return moved;
    })();
    this._preorderReconcilePromise = run;
    try {return await run;} finally {if (this._preorderReconcilePromise === run) this._preorderReconcilePromise = null;}
  }

  async _transferAvailablePreorder(key, preorderEpoch) {
    const result = await this.enqueueSessionOperation('preorder-transfer', async context => {
      const cart = this.preorderCart, original = cart.lines.find(line => line.lineKey === key);
      if (cart.epoch !== preorderEpoch || !original || (original.verification !== 'available' && !original.transferKey) ||
        !this.ordinaryOwnerReady || !['enforced', 'legacy'].includes(this.snapshotMode) || this.snapshotLocked || this._preorderTransferBlocked()) return false;
      const guest = context.authScope === 'guest', matching = this.basketItems.filter(line =>
        line.child_product_id === original.identity.childProductId && line.currentSize === (original.transferSizeName || original.currentSize));
      if (matching.length > 1) return false;
      const existingCount = matching[0]?.count || 0;
      if (guest && original.transferKey && original.verification !== 'available') {
        if (existingCount >= original.transferTargetCount) return cart.removeTransferred(key, original.transferKey, original.quantity);
        cart.clearTransfer(key, original.transferKey);
        return false;
      }
      const prepared = cart.prepareTransfer(key, existingCount + original.quantity);
      if (!prepared) return false;
      const {identity, quantity, transferKey} = prepared, currentSize = prepared.transferSizeName;
      const request = this.captureSnapshotRequest(); this._enforcedMutationRequest = request;
      try {
        let response;
        if (guest) {
          const identities = basketIdentityItems(this.basketItems.map(line => line.status === 'visible'
            ? {...line, sizeId: line.sku.sizeId} : line));
          const index = matching.length ? this.basketItems.indexOf(matching[0]) : -1;
          const count = Math.max(prepared.transferTargetCount, existingCount);
          if (index < 0) identities.push({child_product_id: identity.childProductId, productId: prepared.item?.productId,
            colorId: identity.colorId, sizeId: identity.sizeId, currentSize, count});
          else identities[index] = {...identities[index], count};
          response = await mergeBasketItemsNoUser(identities);
          if (!this._sessionOperationCurrent(context) || !this._currentSnapshotRequest(request)) return false;
          const snapshot = normalizeBasketSnapshot(response, this._session);
          const transferred = snapshot.items.find(line => line.child_product_id === identity.childProductId &&
            line.currentSize === currentSize && line.count >= prepared.transferTargetCount &&
            line.colorId === identity.colorId && (snapshot.mode === 'legacy' ?
              line.item?.child_product_size?.some(size => size.id === identity.sizeId && size.size_name === currentSize) :
              line.status === 'visible' && line.sku?.sizeId === identity.sizeId &&
              getSizeCommerceView(line.item, currentSize, line.count).action === 'buy_now' &&
              line.sku.stock?.state === 'confirmed' && line.sku.stock.quantity >= line.count));
          if (!transferred) {
            const changed = new Error(BASKET_SNAPSHOT_ERROR); changed.stockChanged = true; throw changed;
          }
          if (snapshot.mode === 'legacy') {
            if (!await cart.refresh() || cart.lines.find(line => line.lineKey === key)?.verification !== 'available') {
              const changed = new Error(BASKET_SNAPSHOT_ERROR); changed.stockChanged = true; throw changed;
            }
          }
          // The durable target count makes a retry idempotent if the page
          // closes after saving the ordinary basket but before removing draft.
          localStorage.setItem('basketStorage', serializeBasketIdentities(snapshot.items, this._session));
        } else {
          response = await transferPreorderToBasket({id: identity.childProductId, ...(prepared.item?.productId ? {productId: prepared.item.productId} : {}),
            colorId: identity.colorId, sizeId: identity.sizeId}, context.principalId,
          currentSize, quantity, transferKey);
          if (!this._sessionOperationCurrent(context) || !this._currentSnapshotRequest(request)) return false;
          if (response?.transferCompleted !== true) throw new Error(BASKET_SNAPSHOT_ERROR);
        }
        if (!this.applySnapshot(response, request)) throw new Error(BASKET_SNAPSHOT_ERROR);
        this._persistSnapshotRecovery(false, guest);
        if (guest && this.recoveryError) throw new Error(BASKET_RECOVERY_ERROR);
        return cart.removeTransferred(key, transferKey, quantity);
      } catch (error) {
        if (!this._sessionOperationCurrent(context)) return false;
        if (error?.response?.status === 409 || error?.stockChanged === true) cart.clearTransfer(key, transferKey);
        try {await this.refreshSession();} catch {this._failSnapshotRequest(BASKET_SNAPSHOT_ERROR);}
        cart.error = 'Не удалось обновить товар в корзине. Попробуйте ещё раз.';
        return false;
      } finally {if (this._enforcedMutationRequest === request) this._enforcedMutationRequest = null;}
    });
    return result.status === 'accepted' && result.value === true;
  }

  _resetSessionQueue() {
    this._enforcedMutationRequest = null;
    for (const listener of this._sessionPixelListeners) document.removeEventListener('generalPixelInited', listener);
    this._sessionPixelListeners.clear();
    if (this._sessionQueue) for (const ticket of this._sessionQueue.waiting) ticket.resolve({status: 'discarded'});
    this._sessionQueue = {epoch: this.sessionEpoch, waiting: [], running: false};
  }

  _sessionOperationContext() {
    return Object.freeze({epoch: this.sessionEpoch, authScope: this._session?.authScope ?? null,
      principalId: this._session?.principalId ?? null, userId: this.user.user?.id, isAuth: this.user.isAuth,
      snapshotVersion: this.localSnapshotVersion, requestSeq: this._snapshotRequestSeq});
  }

  _sessionOperationCurrent(context) {
    return context?.epoch === this.sessionEpoch && context.authScope === (this._session?.authScope ?? null) &&
      context.principalId === (this._session?.principalId ?? null) && context.userId === this.user.user?.id && context.isAuth === this.user.isAuth;
  }

  enqueueSessionOperation(kind, work) {
    const failed = {status: 'failed', error: BASKET_SNAPSHOT_ERROR};
    if (!this._session || typeof kind !== 'string' || !kind.length || kind.length > 64 || typeof work !== 'function') return Promise.resolve(failed);
    if (!this._sessionQueue || this._sessionQueue.epoch !== this.sessionEpoch) this._resetSessionQueue();
    const queue = this._sessionQueue;
    if (queue.waiting.length >= 32) return Promise.resolve(failed);
    const context = this._sessionOperationContext();
    const result = new Promise(resolve => queue.waiting.push({context, work, resolve}));
    this._drainSessionQueue(queue);
    return result;
  }

  async _drainSessionQueue(queue) {
    if (queue.running) return;
    queue.running = true;
    try {
      while (queue === this._sessionQueue && queue.waiting.length) {
        const ticket = queue.waiting.shift();
        if (!this._sessionOperationCurrent(ticket.context)) {ticket.resolve({status: 'discarded'}); continue;}
        // Ownership is latched at enqueue; snapshot sequence/version belong to
        // dispatch, after any earlier same-owner refresh has acknowledged.
        const dispatched = Object.freeze({...ticket.context, snapshotVersion: this.localSnapshotVersion, requestSeq: this._snapshotRequestSeq});
        try {
          const value = await ticket.work(dispatched);
          ticket.resolve(this._sessionOperationCurrent(ticket.context) ? {status: 'accepted', value} : {status: 'discarded'});
        } catch (error) {
          if (!this._sessionOperationCurrent(ticket.context)) ticket.resolve({status: 'discarded'});
          else {
            const message = error?.message === BASKET_RECOVERY_ERROR ? BASKET_RECOVERY_ERROR : BASKET_SNAPSHOT_ERROR;
            runInAction(() => {this.snapshotError = message; this.calculatedItems = [];});
            ticket.resolve({status: 'failed', error: message});
          }
        }
      }
    } finally {queue.running = false;}
  }

  queueSessionRefresh(options = {}) {
    const saved = {mergeGuestIdentities: options.mergeGuestIdentities === true};
    return this.enqueueSessionOperation('refresh', () => this.refreshSession(saved)).then(result =>
      result.status === 'accepted' && result.value === false ? {status: 'discarded'} : result);
  }

  async _runLegacySessionOperation(kind, work) {
    if (!this._sessionStarted) return work(this._sessionOperationContext());
    if (this.snapshotLocked || this.snapshotMode !== 'legacy') throw new Error(BASKET_SNAPSHOT_ERROR);
    const result = await this.enqueueSessionOperation(kind, context => {
      if (this.snapshotLocked || this.snapshotMode !== 'legacy') throw new Error(BASKET_SNAPSHOT_ERROR);
      return work(context);
    });
    if (result.status === 'failed') throw new Error(result.error);
    return result.status === 'accepted' ? result.value : false;
  }

  async _runEnforcedBasketMutation(kind, input) {
    if (this._session?.authScope === 'guest') return this._runEnforcedGuestBasketMutation(kind, input);
    const principalId = this._session?.principalId;
    const ready = allowOwnedPending => this._sessionStarted && this._session?.authScope === 'authenticated' &&
      this._session.principalId === principalId && this.user.isAuth === true && this.user.user?.id === principalId &&
      this.snapshotMode === 'enforced' && !this.snapshotError && (!this.snapshotPending ||
        allowOwnedPending && this._sameSnapshotContext(this._enforcedMutationRequest));
    if (!ready(true)) throw new Error(BASKET_SNAPSHOT_ERROR);
    // Pin intent before awaiting the FIFO. Never retain a caller's mutable card,
    // price, cached grant or count as authority for a later dispatch.
    let target, sizeName, identity, chosen;
    if (kind === 'add') {
      if (!Number.isSafeInteger(input.item?.id) || input.item.id <= 0 || input.item.id > 2147483647 ||
        typeof input.currentSize !== 'string' || !/\S/.test(input.currentSize) || input.currentSize.length > 128)
        throw new Error(BASKET_SNAPSHOT_ERROR);
      target = Object.freeze({id: input.item.id}); sizeName = input.currentSize;
    } else if (kind === 'count' || kind === 'remove') {
      const key = input.item?.lineKey, match = typeof key === 'string' && /^basket:([1-9]\d*)$/.exec(key);
      if (!match || !Number.isSafeInteger(Number(match[1])) || Number(match[1]) > 2147483647 ||
        kind === 'count' && !['increase', 'decrease'].includes(input.modifier)) throw new Error(BASKET_SNAPSHOT_ERROR);
      identity = this._lineSelectionIdentity(input.item);
    } else if (kind === 'clear') {
      if (!['all', 'selected', 'active', 'disable'].includes(input.operation)) throw new Error(BASKET_SNAPSHOT_ERROR);
      chosen = this.basketItems.filter(line => {
        const available = !this._expiredBasketLine(line) && isBasketLineAvailable(line);
        return input.operation === 'selected' ? line.selected !== false && available :
          input.operation === 'active' ? available : input.operation === 'disable' ? !available : true;
      }).map(line => this._lineSelectionIdentity(line));
    } else throw new Error(BASKET_SNAPSHOT_ERROR);
    const modifier = input.modifier, clearAll = input.operation === 'all';
    const result = await this.enqueueSessionOperation(kind, async context => {
      if (!ready(false)) throw new Error(BASKET_SNAPSHOT_ERROR);
      const find = selected => this.basketItems.find(line => this._lineSelectionIdentity(line) === selected);
      const payload = line => Object.freeze({...basketIdentityItems([line])[0], lineKey: line.lineKey});
      let send;
      if (kind === 'add') send = () => addToBasket(target, context.principalId, sizeName);
      else if (kind === 'count' || kind === 'remove') {
        const line = find(identity); if (!line) return false;
        if (kind === 'count' && (modifier === 'decrease' && line.count === 1 ||
          modifier === 'increase' && !this.canIncreaseLine(line.lineKey))) return false;
        const current = payload(line);
        send = kind === 'count' ? () => updateCountItemsBasket(modifier, current, context.principalId) :
          () => deleteBaseBasketOneItem(current, context.principalId);
      } else {
        const selected = chosen.map(find).filter(Boolean).map(payload);
        if (!clearAll && !selected.length) return false;
        send = clearAll ? () => forceDeleteAllBasketItems(context.principalId) :
          () => deleteAllBasketItems(selected, context.principalId);
      }
      const request = this.captureSnapshotRequest(); this._enforcedMutationRequest = request;
      try {
        const response = await send();
        if (!this._sessionOperationCurrent(context) || !this._currentSnapshotRequest(request)) return false;
        if (!this.applySnapshot(response, request)) throw new Error(BASKET_SNAPSHOT_ERROR);
        this._persistSnapshotRecovery(false);
        if (kind === 'add') {
          const added = this.basketItems.find(line => line.child_product_id === target.id && line.currentSize === sizeName && line.status === 'visible');
          if (added) {this.setNotificationContent({...added}); this.setActiveNotification(true);}
        }
        return true;
      } catch {
        if (!this._sessionOperationCurrent(context) || !this._sameSnapshotContext(request)) return false;
        this._failSnapshotRequest(BASKET_SNAPSHOT_ERROR);
        throw new Error(BASKET_SNAPSHOT_ERROR);
      } finally {if (this._enforcedMutationRequest === request) this._enforcedMutationRequest = null;}
    });
    if (result.status === 'failed') throw new Error(BASKET_SNAPSHOT_ERROR);
    return result.status === 'accepted' ? result.value : false;
  }

  async _runEnforcedGuestBasketMutation(kind, input) {
    const ready = allowPending => this._sessionStarted && this._session?.authScope === 'guest' &&
      this._session.principalId === null && this.user.isAuth === false && this.snapshotMode === 'enforced' &&
      !this.snapshotError && !this.recoveryError && (!this.snapshotPending ||
        allowPending && this._sameSnapshotContext(this._enforcedMutationRequest));
    if (!ready(true)) throw new Error(BASKET_SNAPSHOT_ERROR);
    const target = kind === 'add' ? {child_product_id: input.item?.id, currentSize: input.currentSize, count: 1} : null;
    if (target) basketIdentityItems([target]);
    if (!['add', 'count', 'remove', 'clear'].includes(kind) ||
      kind === 'count' && !['increase', 'decrease'].includes(input.modifier) ||
      kind === 'clear' && !['all', 'selected', 'active', 'disable'].includes(input.operation)) throw new Error(BASKET_SNAPSHOT_ERROR);
    const identity = input.item && this._lineSelectionIdentity(input.item), modifier = input.modifier;
    const chosen = kind === 'clear' ? new Set(this.basketItems.filter(line => {
      const available = !this._expiredBasketLine(line) && isBasketLineAvailable(line);
      return input.operation === 'all' || (input.operation === 'selected' ? line.selected !== false && available :
        input.operation === 'active' ? available : !available);
    }).map(line => this._lineSelectionIdentity(line))) : null;
    const result = await this.enqueueSessionOperation(kind, async context => {
      if (!ready(false)) throw new Error(BASKET_SNAPSHOT_ERROR);
      const rows = this.basketItems;
      let identities = basketIdentityItems(rows.map(line => line.status === 'visible' ? {...line, sizeId: line.sku.sizeId} : line));
      const index = target ? rows.findIndex(line => line.child_product_id === target.child_product_id && line.currentSize === target.currentSize) :
        rows.findIndex(line => this._lineSelectionIdentity(line) === identity);
      if (kind === 'add') {
        if (index < 0) identities.push({...target});
        else {
          if (!this.canIncreaseLine(rows[index].lineKey)) return false;
          identities[index] = {...identities[index], count: identities[index].count + 1};
        }
      } else if (kind === 'count') {
        if (index < 0 || modifier === 'decrease' && identities[index].count === 1 ||
          modifier === 'increase' && !this.canIncreaseLine(rows[index].lineKey)) return false;
        identities[index] = {...identities[index], count: identities[index].count + (modifier === 'increase' ? 1 : -1)};
      } else if (kind === 'remove') {
        if (index < 0) return false;
        identities.splice(index, 1);
      } else identities = identities.filter((_line, n) => !chosen.has(this._lineSelectionIdentity(rows[n])));
      const request = this.captureSnapshotRequest(); this._enforcedMutationRequest = request;
      try {
        const response = await mergeBasketItemsNoUser(identities);
        if (!this._sessionOperationCurrent(context) || !this._currentSnapshotRequest(request)) return false;
        if (!this.applySnapshot(response, request)) throw new Error(BASKET_SNAPSHOT_ERROR);
        this._persistSnapshotRecovery(false, true);
        if (target) {
          const added = this.basketItems.find(line => line.child_product_id === target.child_product_id &&
            line.currentSize === target.currentSize && line.status === 'visible');
          if (added) {this.setNotificationContent({...added}); this.setActiveNotification(true);}
        }
        return true;
      } catch {
        if (!this._sessionOperationCurrent(context) || !this._sameSnapshotContext(request)) return false;
        this._failSnapshotRequest(BASKET_SNAPSHOT_ERROR);
        throw new Error(BASKET_SNAPSHOT_ERROR);
      } finally {if (this._enforcedMutationRequest === request) this._enforcedMutationRequest = null;}
    });
    if (result.status === 'failed') throw new Error(BASKET_SNAPSHOT_ERROR);
    return result.status === 'accepted' ? result.value : false;
  }

  _legacyContinuationCurrent(context) {
    return this._sessionOperationCurrent(context) && context.snapshotVersion === this.localSnapshotVersion &&
      context.requestSeq === this._snapshotRequestSeq && (!this._sessionStarted ||
        (!this.snapshotLocked && this.snapshotMode === 'legacy'));
  }

  _sessionPixel(context, kind, productId) {
    const send = () => {
      if (!this._legacyContinuationCurrent(context)) return;
      if (kind === 'add') window.turboTargetPixelRTGAddBasket(productId);
      else window.turboTargetPixelRTGDelBasket(productId);
    };
    if (window.turboTarget?.sendRTGMessage) send();
    else {
      const listener = () => {this._sessionPixelListeners.delete(listener); send();};
      this._sessionPixelListeners.add(listener);
      document.addEventListener('generalPixelInited', listener, {once: true});
    }
  }

  _checkoutOperationIdentity() {
    return JSON.stringify([this.selectedItems.map(line => [this._lineSelectionIdentity(line), line.count]),
      this.selectPaymentType, this.promocode, this.bonusesSpend, this.certificates,
      this.delivery.active, this.delivery.price, this.delivery.officePvz, this.delivery.cityInfo,
      this.delivery.customerInfo, this.delivery.selectedAddress, this.orderSearchQuery]);
  }

  pricingInput() {
    const items = this.selectedItems.map(line => ({...basketIdentityItems([{...line, sizeId: line.sku.sizeId}])[0], lineKey: line.lineKey}));
    const delivery = this.delivery.active?.id ?? null, payment = this.selectPaymentType?.id ?? null;
    if (this.delivery.active != null && !pricingId(delivery) || this.selectPaymentType != null && !pricingId(payment) ||
      this.promocode !== null && this.promocode !== undefined && this.promocode !== '' && !pricingText(this.promocode)) pricingFail();
    pricingMinor(this.bonusesSpend ?? 0);
    const result = {items, delivery, payment, promo: this.promocode || null, bonusesAmount: this.bonusesSpend ?? 0};
    if (delivery !== null) {
      const point = this.delivery.officePvz;
      const selected = this.delivery.selectedAddress;
      const address = point ? this.delivery.cityInfo : selected ? selected.addressFields?.all ?? selected.all : this.delivery.customerInfo?.all;
      const data = address?.data, addressId = [data?.fias_id, data?.geoname_id, data?.kladr_id].find(value => pricingText(value));
      const shippingContext = {};
      if (addressId) shippingContext.addressId = addressId;
      if (point) {
        const pointId = point.code ?? point.id;
        if (!pricingText(typeof pointId === 'number' && pricingId(pointId) ? String(pointId) : pointId)) pricingFail();
        shippingContext.pointId = String(pointId);
        if (!addressId && pricingText(address?.value, 300)) shippingContext.addressQuery = address.value;
      } else if (pricingText(address?.value, 300)) shippingContext.addressQuery = address.value;
      if (!shippingContext.addressId && !shippingContext.addressQuery) pricingFail();
      result.shippingContext = shippingContext;
    }
    return result;
  }

  pricingParameters() {
    const selected = this.delivery.selectedAddress;
    const address = selected ? selected.addressFields?.all ?? selected.all : this.delivery.customerInfo?.all;
    const geo = this.delivery.cityInfo?.data;
    try {return JSON.stringify([this.pricingInput(), [geo?.fias_id, geo?.geoname_id, geo?.kladr_id],
      this.delivery.officePvz?.code ?? this.delivery.officePvz?.id, address?.value, address?.data?.fias_id, address?.data?.geoname_id, address?.data?.kladr_id]);}
    catch {return '!invalid';}
  }

  clearPricing() {
    this._ordinaryQuote = null;
    this._pricingReceipt = null; this._pricingContext = null; this.pricingState = 'idle'; this.pricingError = null;
    this.totalPrice = 0; this.priceNoSell = 0; this.delPrice = 0; this.promoDiscountSumm = 0;
    this.promoNitificator = {color: 'red', message: ''}; this.promocodeItem = null;
    this.bonusesOrderEarned = 0; this.bonusesOrderAvail = 0; this.bonusesTotal = 0; this.bonusesConfirmed = 0;
    this.isLoyaltyActive = false; this.usedPromotions = [];
  }

  startPricing(context) {this.clearPricing(); this._pricingContext = context; this.pricingState = 'pending';}

  pricingContextCurrent(context) {
    return context === this._pricingContext && this._sessionOperationCurrent(context) && !this.snapshotLocked && this.snapshotMode === 'enforced' &&
      this.ordinaryOwnerReady &&
      context.snapshotVersion === this.localSnapshotVersion && context.requestSeq === this._snapshotRequestSeq && context.parameters === this.pricingParameters();
  }

  failPricing(context) {if (!this.pricingContextCurrent(context)) return false; this.clearPricing(); this.pricingState = 'error'; this.pricingError = PRICING_ERROR; return false;}

  applyPricing(response, context) {
    if (!this.pricingContextCurrent(context)) return false;
    try {
      const receipt = normalizePricing(response, this.selectedItems, this._session.authScope);
      if (receipt.deliveryState !== (context.deliveryId !== null && receipt.lines.length ? 'selected' : 'not_selected')) pricingFail();
      if (receipt.promocode.status === true && receipt.promocode.value !== this.promocode) pricingFail();
      this._pricingReceipt = receipt; this.pricingState = 'ready'; this.pricingError = null;
      this.totalPrice = receipt.totalPrice; this.priceNoSell = receipt.priceNoSell; this.delPrice = receipt.deliveryPrice;
      this.promoDiscountSumm = receipt.promoDiscountSumm; this.usedPromotions = [...receipt.promotions];
      const loyalty = receipt.loyalty, changes = loyalty?.bonusPointsChanges;
      this.bonusesOrderEarned = changes?.earnedAmount ?? 0; this.bonusesConfirmed = changes?.spentAmount ?? 0;
      this.bonusesOrderAvail = loyalty?.totalBonusPointsInfo.availableAmountForCurrentOrder ?? 0;
      this.bonusesTotal = loyalty?.totalBonusPointsInfo.balance.available ?? 0; this.isLoyaltyActive = loyalty !== null;
      if (receipt.promocode.status === true) this.promoNitificator = {color: 'green', message: 'Применен промокод ' + receipt.promocode.value};
      else if (receipt.promocode.error) this.promoNitificator = {color: 'red', message: receipt.promocode.error};
      return true;
    } catch {return this.failPricing(context);}
  }

  get pricingReceipt() {return this.pricingState === 'ready' && this.pricingContextCurrent(this._pricingContext) ? this._pricingReceipt : null;}
  pricingLine(line) {return this.pricingReceipt?.lines.find(row => row.lineKey === line.lineKey && row.count === line.count) ?? null;}
  retryPricing() {this.pricingRetryVersion += 1;}

  get ordinaryOwnerReady() {
    return this._session?.authScope === 'authenticated' ? this.user.isAuth === true && this.user.user?.id === this._session.principalId :
      this._session?.authScope === 'guest' && this.user.isAuth === false && !this.user.user?.id;
  }

  ordinaryCalculationComplete(input) {
    return Boolean(input.items.length && pricingId(input.delivery) && pricingId(input.payment) && input.shippingContext);
  }

  applyOrdinaryQuote(raw, context) {
    if (!this.pricingContextCurrent(context)) return false;
    try {
      const value = copyPricing(raw);
      if (!pricingKeys(value, ['schemaVersion', 'conditionsHash', 'pricing']) || value.schemaVersion !== 'site-order-quote-v1' ||
        typeof value.conditionsHash !== 'string' || !/^[a-f0-9]{64}$/.test(value.conditionsHash) || !this.ordinaryCalculationComplete(this.pricingInput()) ||
        !this.applyPricing(value.pricing, context) || value.pricing.deliveryState !== 'selected') pricingFail();
      this._ordinaryQuote = Object.freeze({conditionsHash: value.conditionsHash, calculation: copyPricing(this.pricingInput())});
      return true;
    } catch {return this.failPricing(context);}
  }

  get ordinaryQuote() {return this.pricingReceipt ? this._ordinaryQuote : null;}
  get ordinaryOrderReceipt() {return this._sessionOperationCurrent(this._ordinaryReceiptContext) ? this._ordinaryOrderReceipt : null;}
  get ordinaryCheckoutBusy() {return ['pending', 'recovering'].includes(this.ordinaryCheckoutState);}
  get ordinaryCertificateUnsupported() {return this._useCertificate || this.certificateTotal > 0 ||
    this.certificates.some(value => value && typeof value === 'object' && (value.isActive || value.value));}
  get ordinaryCheckoutCanAdvance() {
    return !this.snapshotLocked && this.snapshotMode === 'enforced' && this.ordinaryOwnerReady && !this.ordinaryCheckoutBusy &&
      !this._ordinaryIntent && !this.ordinaryCheckoutError && Boolean(this.pricingReceipt?.lines.length) &&
      !this.ordinaryCertificateUnsupported &&
      (this.startBasketNum === 1 || Boolean(this.ordinaryQuote)) &&
      (this.startBasketNum !== 4 || this.delivery.customerInfo.agreePrivacy === true);
  }

  _ordinarySlot() {return `yw:ordinary-checkout:v1:${this._session.authScope}:${this._session.principalId ?? 'guest'}`;}
  _loadOrdinaryIntent() {
    try {
      const raw = localStorage.getItem(this._ordinarySlot()); if (raw === null) return;
      if (raw.length > 1048576) pricingFail();
      const value = copyPricing(JSON.parse(raw));
      if (!pricingKeys(value, ['schemaVersion', 'scope', 'principalId', 'command', 'pricing',
        ...(Object.hasOwn(value, 'guestBasketWitness') ? ['guestBasketWitness'] : [])]) || value.schemaVersion !== 'site-ordinary-checkout-intent-v1' ||
        value.scope !== this._session.authScope || value.principalId !== this._session.principalId) pricingFail();
      this._validateOrdinaryIntent(value);
      this._ordinaryIntent = value; this.ordinaryCheckoutState = 'unknown'; this._ordinaryNeedsRecovery = true;
    } catch {this.ordinaryCheckoutState = 'error'; this.ordinaryCheckoutError = 'Не удалось проверить сохранённое оформление. Заказ не отправлен повторно.';}
  }

  _validateOrdinaryIntent(value) {
    const command = value.command;
    if (Object.hasOwn(value, 'guestBasketWitness')) {
      if (value.scope !== 'guest' || typeof value.guestBasketWitness !== 'string') pricingFail();
      const recovery = readBasketRecovery(value.guestBasketWitness, {authScope: 'guest', principalId: null});
      if (recovery.provenance !== 'guest' || serializeBasketIdentities(recovery.items, {authScope: 'guest', principalId: null}) !== value.guestBasketWitness) pricingFail();
    }
    if (!pricingKeys(command, ['idempotencyKey', 'expectedConditionsHash', 'calculation', 'recipient', 'avoidCall',
      ...(Object.hasOwn(command, 'deviceUUID') ? ['deviceUUID'] : [])]) ||
      Object.hasOwn(command, 'deviceUUID') && !ordinaryDeviceUUID(command.deviceUUID) ||
      typeof command.idempotencyKey !== 'string' || !/^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/.test(command.idempotencyKey) ||
      typeof command.expectedConditionsHash !== 'string' || !/^[a-f0-9]{64}$/.test(command.expectedConditionsHash) || typeof command.avoidCall !== 'boolean' ||
      !pricingRecord(command.calculation) || !Array.isArray(command.calculation.items) || !pricingId(command.calculation.payment) ||
      !pricingId(command.calculation.delivery) || !pricingRecord(command.calculation.shippingContext) ||
      !pricingKeys(command.recipient, ['name', 'surname', 'patronymic', 'telephone', 'email', 'floor', 'front_door', 'intercom', 'flat'])) pricingFail();
    if (Object.keys(command.calculation).some(key => !['items', 'delivery', 'payment', 'promo', 'bonusesAmount', 'shippingContext'].includes(key)) ||
      command.calculation.items.length === 0 || command.calculation.items.length > 500 ||
      Object.keys(command.calculation.shippingContext).some(key => !['addressId', 'addressQuery', 'pointId'].includes(key)) ||
      !command.calculation.shippingContext.addressId && !command.calculation.shippingContext.addressQuery) pricingFail();
    basketIdentityItems(command.calculation.items);
    for (const row of command.calculation.items) if (Object.keys(row).some(key =>
      !['child_product_id', 'currentSize', 'count', 'productId', 'colorId', 'sizeId', 'lineKey'].includes(key)) || !pricingText(row.lineKey, 400)) pricingFail();
    for (const [key, field] of Object.entries(command.calculation.shippingContext)) if (!pricingText(field, key === 'addressQuery' ? 300 : 255)) pricingFail();
    if (command.calculation.promo != null && !pricingText(command.calculation.promo)) pricingFail();
    if (command.calculation.bonusesAmount !== undefined) pricingMinor(command.calculation.bonusesAmount);
    if (value.scope === 'guest' && (command.calculation.bonusesAmount ?? 0) !== 0) pricingFail();
    for (const [key, field] of Object.entries(command.recipient)) {
      if (field !== null && !pricingText(field, key === 'email' ? 254 : 100)) pricingFail();
    }
    if (!command.recipient.name || !/^\+[1-9]\d{7,14}$/.test(command.recipient.telephone) ||
      command.recipient.email !== null && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(command.recipient.email)) pricingFail();
    if (normalizePricing(value.pricing, command.calculation.items, value.scope).deliveryState !== 'selected') pricingFail();
  }

  _ordinaryReceipt(raw, value) {
    const receipt = copyPricing(raw), order = receipt.order;
    if (!pricingKeys(receipt, ['status', 'order']) || receipt.status !== 'success' || !pricingKeys(order,
      ['id', 'custom_order_id', 'order_number', 'result_price', 'deliveryPrice', 'deliveryId', 'paymentmethodId']) ||
      !pricingId(order.id) || !pricingId(order.custom_order_id) || !pricingId(order.order_number) ||
      order.deliveryId !== value.command.calculation.delivery || order.paymentmethodId !== value.command.calculation.payment ||
      pricingMinor(order.result_price) !== pricingMinor(value.pricing.totalPrice) || pricingMinor(order.deliveryPrice) !== pricingMinor(value.pricing.deliveryPrice)) pricingFail();
    return receipt; // Creation ACK is not a payment fact.
  }

  _acceptOrdinaryReceipt(raw, value) {
    const receipt = this._ordinaryReceipt(raw, value);
    this.ordinaryCheckoutState = 'accepted'; this.ordinaryCheckoutError = null;
    this._ordinaryReceiptContext = this._sessionOperationContext(); this._ordinaryOrderReceipt = receipt;
    // Authenticated rows were consumed in the server's Order transaction. Never
    // subtract locally: a same-SKU addition after COMMIT is a new cart fact.
    // A guest has no SQL cart; consume only the exact persisted draft version.
    if (value.scope === 'guest') this._consumeAcceptedGuestDraft(value);
    this.snapshotPending = true;
    void this.queueSessionRefresh();
    // Only the acknowledged immutable intent may be removed. A failed cleanup
    // does not hide the saved order, and leaves recovery available on reload.
    try {
      const expected = JSON.stringify(value);
      if (localStorage.getItem(this._ordinarySlot()) === expected) localStorage.removeItem(this._ordinarySlot());
    } catch {}
    return receipt;
  }

  _consumeAcceptedGuestDraft(value) {
    if (!value.guestBasketWitness || this._session?.authScope !== 'guest') return false;
    try {
      const rows = this._basketItems.map(line => line.status === 'visible' ? {...line, sizeId: line.sku.sizeId} : line);
      if (localStorage.getItem('basketStorage') !== value.guestBasketWitness ||
        serializeBasketIdentities(rows, this._session) !== value.guestBasketWitness) return false;
      const selected = value.command.calculation.items;
      const remaining = rows.filter(line => !selected.some(accepted => accepted.lineKey === line.lineKey &&
        accepted.child_product_id === line.child_product_id && accepted.currentSize === line.currentSize && accepted.count === line.count &&
        accepted.colorId === line.colorId && accepted.productId === line.productId));
      localStorage.setItem('basketStorage', serializeBasketIdentities(remaining, this._session));
      this._loadBasketRecovery();
      return true;
    } catch {this.recoveryError = BASKET_RECOVERY_ERROR; return false;}
  }

  async recoverOrdinaryCheckout() {
    if (!this._ordinaryIntent || this.ordinaryCheckoutBusy || !this.ordinaryOwnerReady || this.snapshotLocked) return false;
    const value = this._ordinaryIntent, context = this._sessionOperationContext();
    this._ordinaryNeedsRecovery = false; this.ordinaryCheckoutState = 'recovering'; this.ordinaryCheckoutError = null;
    try {
      const raw = await recoverOrdinaryOrder(value.command, {authScope: context.authScope});
      if (!this._sessionOperationCurrent(context) || this._ordinaryIntent !== value) return false;
      if (pricingKeys(raw, ['status']) && raw.status === 'not_found') {this.ordinaryCheckoutState = 'not_found'; return false;}
      return this._acceptOrdinaryReceipt(raw, value);
    } catch {
      if (!this._sessionOperationCurrent(context) || this._ordinaryIntent !== value) return false;
      this.ordinaryCheckoutState = 'unknown'; this.ordinaryCheckoutError = 'Результат оформления неизвестен. Проверьте заказ, не отправляйте новый.'; return false;
    }
  }

  async _createOrdinaryCheckout(context, retry = false) {
    if (!this._sessionOperationCurrent(context) || !this.ordinaryOwnerReady || this.snapshotLocked || this.ordinaryCheckoutBusy) return false;
    let value = this._ordinaryIntent;
    const slot = this._ordinarySlot();
    try {
      if (retry) {
        if (this.ordinaryCheckoutState !== 'not_found' || !value) return false;
        if (context.authScope === 'guest' && (!this._confirmedUserPhone ||
          ordinaryTelephone(this.delivery.customerInfo.telephone || '') !== value.command.recipient.telephone)) {
          this.delivery.setTelephoneValidShield('Подтвердите номер телефона для повторного оформления.');
          return false;
        }
      } else {
        if (!this.ordinaryCheckoutCanAdvance || !this.ordinaryQuote) return false;
        const bytes = new Uint8Array(16); window.crypto.getRandomValues(bytes);
        bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
        const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
        const key = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
        const info = this.delivery.customerInfo, address = this.delivery.selectedAddress?.addressFields ?? info;
        const nullable = field => field === '' || field === undefined || field === null ? null : String(field);
        const recipient = {name: info.name, surname: nullable(info.surname), patronymic: nullable(info.patronymic),
          telephone: ordinaryTelephone(info.telephone), email: nullable(info.email)};
        for (const field of ['floor', 'front_door', 'intercom', 'flat']) recipient[field] = nullable(address[field] ?? (field === 'flat' ? address.apartment : null));
        // Metadata is captured once with this immutable command, never read
        // again for recovery/retry and never used as customer identity.
        let deviceUUID;
        try {deviceUUID = getMindboxDeviceUUID();} catch { /* missing tracking must not prevent an order */ }
        value = copyPricing({schemaVersion: 'site-ordinary-checkout-intent-v1', scope: context.authScope, principalId: context.principalId,
          command: {idempotencyKey: key, expectedConditionsHash: this.ordinaryQuote.conditionsHash,
            calculation: this.ordinaryQuote.calculation, recipient, avoidCall: this.avoidCall,
            ...(ordinaryDeviceUUID(deviceUUID) ? {deviceUUID} : {})}, pricing: this.pricingReceipt,
          ...(context.authScope === 'guest' ? {guestBasketWitness: serializeBasketIdentities(this._basketItems.map(line =>
            line.status === 'visible' ? {...line, sizeId: line.sku.sizeId} : line), this._session)} : {})});
        this._validateOrdinaryIntent(value);
        if (localStorage.getItem(slot) !== null) throw Error();
        localStorage.setItem(slot, JSON.stringify(value));
      }
      if (localStorage.getItem(slot) !== JSON.stringify(value) || !this._sessionOperationCurrent(context) || !this.ordinaryOwnerReady) throw Error();
      this._ordinaryIntent = value; this.ordinaryCheckoutState = 'pending'; this.ordinaryCheckoutError = null;
    } catch {this.ordinaryCheckoutState = 'error'; this.ordinaryCheckoutError = 'Не удалось сохранить ключ оформления. Заказ не отправлен.'; return false;}
    try {
      const raw = await createOrdinaryOrder(value.command, {authScope: context.authScope});
      if (!this._sessionOperationCurrent(context) || this._ordinaryIntent !== value) return false;
      return this._acceptOrdinaryReceipt(raw, value);
    } catch {
      if (!this._sessionOperationCurrent(context) || this._ordinaryIntent !== value) return false;
      this.ordinaryCheckoutState = 'unknown'; this.ordinaryCheckoutError = 'Результат оформления неизвестен. Проверьте заказ, не отправляйте новый.';
      return false;
    }
  }

  retryOrdinaryCheckout() {return this._createOrdinaryCheckout(this._sessionOperationContext(), true);}

  refreshOrdinaryCheckout() {
    if (!this._ordinaryIntent || this.ordinaryCheckoutState !== 'not_found' || !this.ordinaryOwnerReady || this.snapshotLocked) return false;
    const context = this._sessionOperationContext(), slot = this._ordinarySlot(), expected = JSON.stringify(this._ordinaryIntent);
    try {
      if (localStorage.getItem(slot) !== expected || !this._sessionOperationCurrent(context)) throw Error();
      localStorage.removeItem(slot);
      if (localStorage.getItem(slot) !== null || !this._sessionOperationCurrent(context)) throw Error();
      this._ordinaryIntent = null; this.ordinaryCheckoutState = 'idle'; this.ordinaryCheckoutError = null;
      this.clearPricing(); this.retryPricing(); // Explicit new calculation only, never automatic create.
      return true;
    } catch {this.ordinaryCheckoutError = 'Не удалось обновить сохранённое оформление. Новый заказ не отправлен.'; return false;}
  }

  _clearSnapshotExpiry() {
    if (this._snapshotExpiryTimer !== null) clearTimeout(this._snapshotExpiryTimer);
    this._snapshotExpiryTimer = null;
  }

  _loadBasketRecovery() {
    this.recoverableIdentities = [];
    this.recoveryProvenance = 'none';
    this.recoveryError = null;
    try {
      const recovery = readBasketRecovery(localStorage.getItem('basketStorage'), this._session);
      this.recoverableIdentities = recovery.items;
      this.recoveryProvenance = recovery.provenance;
    } catch {
      this.recoveryError = BASKET_RECOVERY_ERROR;
    }
  }

  captureSnapshotRequest() {
    if (!this._session) throw new Error(BASKET_SNAPSHOT_ERROR);
    this._snapshotRequestSeq += 1;
    this.snapshotPending = true;
    this.snapshotError = null;
    this.calculatedItems = [];
    if (this.snapshotMode === 'enforced') this.clearPricing();
    return {epoch: this.sessionEpoch, ...this._session, requestSeq: this._snapshotRequestSeq};
  }

  _currentSnapshotRequest(context) {
    return this._sameSnapshotContext(context) && context.requestSeq > this._snapshotSettledSeq;
  }

  _sameSnapshotContext(context) {
    return Boolean(context && this._session && context.epoch === this.sessionEpoch &&
      context.authScope === this._session.authScope && context.principalId === this._session.principalId &&
      context.requestSeq === this._snapshotRequestSeq && context.requestSeq > 0);
  }

  _lineSelectionIdentity(line) {
    return JSON.stringify([line.lineKey, line.child_product_id ?? line.item?.id, line.currentSize,
      line.productId ?? line.item?.productId, line.colorId ?? line.item?.colorId, line.sku?.sizeId]);
  }

  applySnapshot(envelope, context) {
    if (!this._currentSnapshotRequest(context)) return false;
    try {
      const snapshot = normalizeBasketSnapshot(envelope, this._session);
      if (this.snapshotMode === 'enforced' && snapshot.mode !== 'enforced') throw new Error(BASKET_SNAPSHOT_ERROR);
      const selected = new Map(this._basketItems.map(line => [this._lineSelectionIdentity(line), line.selected !== false]));
      this._basketItems = snapshot.items.map(line => ({...line, selected: selected.get(this._lineSelectionIdentity(line)) ?? true}));
      this.snapshotMode = snapshot.mode;
      this.snapshotPending = false;
      this.snapshotError = null;
      this._snapshotSettledSeq = context.requestSeq;
      this.localSnapshotVersion += 1;
      // A late reply cannot undo an expiry already observed in this epoch.
      this._snapshotClock = Math.max(this._snapshotClock, Date.now());
      this.update = true;
      this.calculatedItems = [];
      this.updateSelected();
      this._scheduleBasketExpiry();
      return true;
    } catch {
      if (envelope?.publicSku?.mode === 'enforced') this.snapshotMode = 'enforced';
      this.snapshotPending = false;
      this.snapshotError = BASKET_SNAPSHOT_ERROR;
      this._snapshotSettledSeq = context.requestSeq;
      return false;
    }
  }

  async refreshSession({mergeGuestIdentities = false} = {}) {
    if (!this._session) throw new Error(BASKET_SNAPSHOT_ERROR);
    const context = this.captureSnapshotRequest();
    try {
      const guest = context.authScope === 'guest';
      if (guest && this.recoveryError) throw new Error(BASKET_RECOVERY_ERROR);
      const mergeGuest = !guest && mergeGuestIdentities === true && !this._guestRecoveryConsumed && this.recoveryProvenance === 'guest';
      const identities = guest && ['guest', 'legacy_unscoped'].includes(this.recoveryProvenance) || mergeGuest ?
        this.recoverableIdentities.map(line => ({...line})) : [];
      const response = guest ? await mergeBasketItemsNoUser(identities) : await getAndMergeBasketItems(context.principalId, identities);
      if (!this._currentSnapshotRequest(context)) return false;
      if (!this.applySnapshot(response, context)) throw new Error(BASKET_SNAPSHOT_ERROR);
      this._persistSnapshotRecovery(mergeGuest);
      return true;
    } catch (error) {
      if (!this._sameSnapshotContext(context)) return false;
      const message = error?.message === BASKET_RECOVERY_ERROR ? BASKET_RECOVERY_ERROR : BASKET_SNAPSHOT_ERROR;
      this._failSnapshotRequest(message);
      throw new Error(message);
    }
  }

  _failSnapshotRequest(message) {
    this.snapshotPending = false;
    this.snapshotError = message;
    this.calculatedItems = [];
    this._snapshotSettledSeq = this._snapshotRequestSeq;
  }

  _persistSnapshotRecovery(mergeGuest, migrateLegacyGuest = false) {
    if (mergeGuest) this._guestRecoveryConsumed = true;
    // An explicit guest mutation may migrate legacy identities after the server
    // ACK. Automatic/authenticated refresh cannot replace unscoped recovery.
    const guestMigration = migrateLegacyGuest === true && this._session?.authScope === 'guest' &&
      this.snapshotMode === 'enforced' && !this.snapshotPending && !this.snapshotError;
    if (!this.recoveryError && this.recoveryProvenance !== 'foreign_owner' &&
      (this.recoveryProvenance !== 'legacy_unscoped' || guestMigration)) {
      try {
        // Only accepted enforced rows have a structurally bound selected SKU.
        // General recovery/serialization must never infer claims from cached cards.
        const identities = this._basketItems.map(line => this.snapshotMode === 'enforced' &&
          line.status === 'visible' && !Object.prototype.hasOwnProperty.call(line, 'sizeId') ?
          {...line, sizeId: line.sku.sizeId} : line);
        localStorage.setItem('basketStorage', serializeBasketIdentities(identities, this._session));
        this._loadBasketRecovery();
      } catch {this.recoveryError = BASKET_RECOVERY_ERROR;}
    }
  }

  _scheduleBasketExpiry() {
    this._clearSnapshotExpiry();
    const expiry = nextBasketExpiry(this._basketItems);
    if (expiry === null) return;
    const epoch = this.sessionEpoch;
    this._snapshotExpiryTimer = setTimeout(action(() => {
      if (epoch !== this.sessionEpoch) return;
      this._snapshotClock = Math.max(this._snapshotClock, Date.now());
      this.calculatedItems = [];
      this.update = true;
      this.updateSelected();
      this._scheduleBasketExpiry();
    }), Math.min(expiry - Date.now() + 1, 2147483647));
  }

  get snapshotLocked() {
    return Boolean(this._sessionStarted && (!this._session || this.snapshotMode === 'unknown' || this.snapshotPending || this.snapshotError));
  }

  get displayLineCount() {return this.basketItems.length;}
  get displayQuantity() {return this.totalCountBasketItems;}
  get checkoutLineCount() {return this.selectedItems.length;}
  get checkoutQuantity() {return this.selectedItems.reduce((sum, line) => sum + line.count, 0);}

  canIncreaseLine(lineKey) {
    // Reading the observable clock makes expiry visible to MobX consumers.
    void this._snapshotClock;
    const line = this.basketItems.find(item => item.lineKey === lineKey);
    if (!line || this._expiredBasketLine(line)) return false;
    return canIncreaseBasketLine(this.basketItems, lineKey, this.snapshotLocked);
  }

  _expiredBasketLine(line) {
    const expiry = getSizeCommerceView(line.item, line.currentSize, line.count).expiresAt;
    return this._session !== null && expiry !== null && expiry < this._snapshotClock;
  }

  init() {
    this._pricingReceipt = null; this._pricingContext = null; this.pricingState = 'idle'; this.pricingError = null;
    this._ordinaryQuote = null; this._ordinaryIntent = null; this._ordinaryNeedsRecovery = false;
    this.ordinaryCheckoutState = 'idle'; this.ordinaryCheckoutError = null; this._ordinaryOrderReceipt = null; this._ordinaryReceiptContext = null;
    // Счетчик для шапки, чтобы не вызывать каждый раз метод пересчета, иначе цифра будет мигать.
    this._countItems = 0;
    // Всегда актуальные товары в лайв режиме
    this._basketItems = [];
    this._basketPriceNoSell = 0;
    this._basketPriceCalculated = 0;
    this._totalCountBasketItems = 0;
    this._activeNotification = false;
    this._notificationContent = {};
    this._bonusesValue = "";
    this._bonusesSpend = 0;
    this._bonusesInfo = {
      color: "red",
      message: "",
    };
    this.calculatedItems = [];
    //Используется для того, чтобы во время регистрации / авторизации
    //Пользователя не дропнуло в личный кабинет во время оформления заказа
    this._registrationOnFlyBasket = false;
    /// order
    this._startBasketNum = 1;
    this._information = [
      {
        num: 1,
        name: "Товары, которые вы добавили в корзину",
        br_name: "корзина",
        vis_name: "Корзина",
        button_text: "Оформить заказ",
      },
      {
        num: 2,
        name: "Оформление заказа",
        br_name: "Оформление заказа",
        vis_name: "Доставка",
        button_text: "Далее",
      },
      {
        num: 3,
        name: "Оформление заказа",
        br_name: "Оформление заказа",
        vis_name: "Получатель",
        button_text: "Далее",
      },
      {
        num: 4,
        name: "Оформление заказа",
        br_name: "Оформление заказа",
        vis_name: "Оплата",
        button_text: "Оформить заказ",
      },
    ];
    // Функциональное окно выбора горордов / выбора дотставки сдек 2
    this._functionalModalState = 0;
    // Если сумма заказа больше 80 000 то отключаем кнопку оформить заказ.
    this._basketOverPrice = false;
    //ИСПОЛЬЗУЕТСЯ для установки ответа интеграции сдека по стоимости доставки из города в город
    //ИСПОЛЬЗУЕТСЯ ПРИ РАСЧЕТАХ
    this._deliveryPrice = 0;
    this._hasGift = false;
    // Копия значения у "переменной" поиска для страницы адреса доставки
    this._orderSearchQuery = null;
    this._activeBasketItems = [];
    this._disabledBasketItems = [];
    this._selectPaymentType = null;
    this._selectPaymentTypeError = "";
    this._countryAndMask = {};
    this._promocode = null;
    this._findUserByNumber = "";
    this._avoidCall = false;
    this._totoalBonusesAvailable = 0;
    this._bonusesAvailableForBasket = 0;
    this._mindboxOrderAmount = 0;
    this.usedPromotions = [];
    this.bonusesOrderEarned = 0;
    this.bonusesOrderAvail = 0;
    this.bonusesTotal = 0;
    this.bonusesSpendAll = false;
    this.certificateTotal = 0;
    this._bonusesInfo = {
      color: "red",
      message: "",
    };
    this._noProductsForBonuses = false;
    this._confirmedUserPhone = false;
    this._favoriteBasketProduct = [];
    this._useCertificate = false
  }

  addCertificate() {
    this.certificates.push({
      value: "",
      isActive: false,
      toggle: false,
      message: "",
      loading: false,
      amount: 0,
    });
  }

  setCertificate(index, newData) {
    if (newData.value) {
      const exists = this.certificates.some(
        (cert, i) => cert.value === newData.value && i !== index
      );

      if (exists) {
        this.certificates[index] = {
          ...this.certificates[index],
          message: "Сертификат уже добавлен",
        };
        return;
      }
    }

    this.certificates[index] = {
      ...this.certificates[index],
      ...newData,
    };

    this.calculateCrtificate()
  }

  calculateCrtificate() {
    let certificatesTotal = this.certificates
        .filter((c) => c.isActive)
        .reduce((acc, c) => acc + (c.amount || 0), 0);

    this.certificateTotal = certificatesTotal
  }

  removeCertificate(index) {
    this.certificates.splice(index, 1);
    this.calculateCrtificate()
  }

  setTotalCountBasketItems(number) {
    this._totalCountBasketItems = number;
  }

  setUserPhoneConfirm(status) {
    this._confirmedUserPhone = status;
  }
  setFindUserByNumber(string) {
    this._findUserByNumber = string;
  }

  setPromocode(object) {
    this.update = true;
    this._promocode = object;
  }

  setSelectPaymentType(object) {
    this._selectPaymentType = object;
  }

  setSelectPaymentTypeError(string) {
    this._selectPaymentTypeError = string;
  }

  setSavoriteItems(items) {
    this._favoriteBasketProduct = items;
  }

  setDeliveryPrice(number) {
      this._deliveryPrice = number;
  }

  setUpdate() {
      this.update = true;
  }

  setAvoidCall(boolean) {
      this._avoidCall = boolean;
  }

  setOrderSearchQuery(object) {
    this._orderSearchQuery = object;
  }

  setCountryAndMask(object) {
    this._countryAndMask = object;
  }

  setActiveBasketItems(array) {
    this.update = true;
    const selected = new Set(array.map(line => this._lineSelectionIdentity(line)));
    this._basketItems.forEach(line => {line.selected = selected.has(this._lineSelectionIdentity(line));});
    this.updateSelected();
  }

  setDisabledBasketItems(array) {
    this._disabledBasketItems = array;
  }

  setCountItems(number) {
    this._countItems = number;
  }

  setItemsPrices(array) {
    if (this._sessionStarted && (!this._session || this.snapshotMode !== 'legacy')) return false;
    this.update = false;
    if (array) {
      this.setBasket([...array]);
    }
  }

  setBasketItems(array) {
    if (this._sessionStarted && (!this._session || this.snapshotMode !== 'legacy')) return false;
    this._basketItems = Array.isArray(array) ? array : [];
    return true;
  }

  setActiveNotification(bool) {
    this._activeNotification = bool;
  }

  setNotificationContent(object) {
    this._notificationContent = object;
  }

  setRegistrationOnFlyBasket(bool) {
    this._registrationOnFlyBasket = bool;
  }

  setStartBasketNum(number) {
    this._startBasketNum = number;
  }

  setBasketOverPrice(boolean) {
    this._basketOverPrice = boolean;
  }

  setBonusesValue(value) {
    this._bonusesValue = value;
  }

  setBonusesSpend(value) {
    this.update = true;
    this._bonusesSpend = value;
  }

  setBonusesInfo(object) {
    this._bonusesInfo = object;
  }

  //1 - выводит модалку с выбором города
  //2 - модалку сдека с ПВЗ и картой
  setFunctionalModalState(number) {
    this._functionalModalState = number;
  }

  setBasketPriceNoSell(number) {
    this._basketPriceNoSell = number;
  }

  setBasketPriceCalculated(number) {
    this._basketPriceCalculated = number;
  }

  clearPromoNotificator() {
    this.promoNitificator.message = "";
  }

  clearBonusesInfo() {
    this.bonusesInfo.message = "";
  }

  setCalculatedItems(array) {
    this.calculatedItems = array;
  }

  setNoProductsForBonuses(boolean) {
    this._noProductsForBonuses = boolean;
  }

  selectAll(state) {
    this.update = true;
    this._basketItems = this.basketItems.map((item) => {
      item.selected = state;
      return item;
    });
    this.updateSelected();
  }

  updateSelected() {
    this._activeBasketItems = this.selectedItems;
  }

  get totalCountBasketItems() {
    return this.basketItems.reduce((accum, item) => accum + item.count, 0);
  }

  get promocode() {
    return this._promocode;
  }

  get allPrice() {
    let price = 0;
    this.selectedItems.forEach((item) => {
      price +=
        (item.item.sell_price > 0
          ? item.item.sell_price
          : item.item.product.price) * item.count;
    });
    return price;
  }

  get basketOverPrice() {
    return this._basketOverPrice;
  }

  get favoriteItems() {
    return this._favoriteBasketProduct;
  }

    get selectPaymentType() {
        return this._selectPaymentType;
    }

  get activeBasketItems() {
    const result = this.divideBasket(this.basketItems).active;
    return result;
  }

  get bonusesValue() {
    return this._bonusesValue;
  }

  get selectedItems() {
    void this._snapshotClock;
    return selectedBasketPurchaseLines(this.basketItems.filter(line => !this._expiredBasketLine(line)), this.snapshotLocked);
  }

  get unSelectedItems() {
    return this.basketItems.filter((item) => !item.selected);
  }

  get bonusesSpend() {
    return this._bonusesSpend;
  }

  get bonusesInfo() {
    return this._bonusesInfo;
  }

  get disabledBasketItems() {
    return this.divideBasket(this.basketItems).disabled;
  }

  get orderSearchQuery() {
    return this._orderSearchQuery;
  }

  get countryAndMask() {
    return this._countryAndMask;
  }

  get selectPaymentTypeError() {
    return this._selectPaymentTypeError;
  }

  get hasGift() {
    return this.selectedItems.some(line => line.item?.product?.electron_sertificate === true);
  }

  get hasGiftOnly() {
    const lines = this.selectedItems;
    return lines.length > 0 && lines.every(line => line.item?.product?.electron_sertificate === true);
  }

  get basketPriceNoSell() {
    return this._basketPriceNoSell;
  }

  get basketPriceCalculated() {
    return this._basketPriceCalculated;
  }

  get deliveryPrice() {
    return this._deliveryPrice;
  }

  get countItems() {
    const preorderCount = process.env.REACT_APP_CONFIRMED_PREORDER_CART_ENABLED === 'true' &&
      process.env.REACT_APP_CONFIRMED_PREORDER_QUOTE_ENABLED === 'true' ? this.preorderCart.lines.length : 0;
    return (this.activeBasketItems?.length || 0) + preorderCount;
  }

  get basketItems() {
    return this._basketItems;
  }

  get activeNotification() {
    return this._activeNotification;
  }

  get notificationContent() {
    return this._notificationContent;
  }

  get registrationOnFlyBasket() {
    return this._registrationOnFlyBasket;
  }

  get startBasketNum() {
    return this._startBasketNum;
  }

  get information() {
    return this._information;
  }

  get functionalModalState() {
    return this._functionalModalState;
  }

  get findUserByNumber() {
    return this._findUserByNumber;
  }

  get avoidCall() {
    return this._avoidCall;
  }

  get noProductsForBonuses() {
    return this._noProductsForBonuses;
  }

  addProduct = (item, currentSize) => this._sessionStarted && this.snapshotMode === 'enforced'
    ? this._runEnforcedBasketMutation('add', {item, currentSize})
    : this._runLegacySessionOperation('add', context => this._legacyAddProduct(item, currentSize, context));

  _legacyAddProduct = async (item, currentSize, context) => {
    if (this.user.isAuth) {
      const response = await addToBasket(item, this.user.user.id, currentSize);
      if (!this._legacyContinuationCurrent(context)) return false;
      if (!response.error && response.items) {
        this.setBasket(response.items);
        this.basketItems.forEach((itemSearch) => {
          if (
            itemSearch.child_product_id === item.id &&
            itemSearch.currentSize === currentSize &&
            itemSearch.colorId === item.colorId
          ) {
            this.setActiveNotification(true);
            this.setNotificationContent({
              item: itemSearch.item,
              currentSize: itemSearch.currentSize,
              count: itemSearch.count,
              colorId: itemSearch.colorId,
              id: itemSearch.id,
            });
          }
        });
      }
    } else {
      let basketItems = [...this.basketItems];
      if (!basketItems?.length) {
        basketItems = [
          {
            item: item,
            currentSize: currentSize,
            count: 1,
            colorId: item.colorId,
            id: item.id,
          },
        ];
        this.setBasket(basketItems);
        this.setActiveNotification(true);
        this.setNotificationContent({
          item: item,
          currentSize: currentSize,
          count: 1,
          colorId: item.colorId,
          id: item.id,
        });
      } else {
        let isFound = false;
        basketItems = basketItems.map((itemForSearch, index) => {
          if (
            itemForSearch.item.id === item.id &&
            itemForSearch.currentSize === currentSize
          ) {
            isFound = true;
            this.setNotificationContent({
              item: itemForSearch.item,
              count: itemForSearch.count + 1,
              currentSize: itemForSearch.currentSize,
              colorId: itemForSearch.colorId,
            });

            return { ...itemForSearch, count: itemForSearch.count + 1 };
          }
          return { ...itemForSearch };
        });

        if (!isFound) {
          basketItems.push({
            item: item,
            currentSize: currentSize,
            count: 1,
            colorId: item.colorId,
            id: item.id,
          });
        }

        this.setBasket(basketItems);
        this.setActiveNotification(true);
        if (!isFound) {
          this.setNotificationContent({
            item: item,
            currentSize: currentSize,
            count: 1,
            colorId: item.colorId,
            id: item.id,
          });
        }
      }
    }
    const categoriesResponse = await getCategories(
      item.product.product_category
    );
    if (!this._legacyContinuationCurrent(context)) return false;
    if (categoriesResponse.success && item.child_product_size) {
      const category = item.product.product_category
        .map((id) => categoriesResponse.data[id])
        .join("/");
      const findSize = item.child_product_size.find(
        (item) => item.size_name === currentSize
      );
      if (findSize) {
        window.dataLayer.push({
          event: "ym-ecom",
          eCategory: "ecommerce",
          eAction: "add",
          ecommerce: {
            currencyCode: "RUB",
            add: {
              products: [
                {
                  id: String(item.id) + String(item.color.id) + findSize.id,
                  name: item.product.name,
                  price: item.sell_price ? item.sell_price : item.parrent_price,
                  variant: item.color.color_name,
                  category,
                  quantity: 1,
                },
              ],
            },
          },
        });
      }
    }

    this._sessionPixel(context, 'add', item.product.id);
  };

  setBasket = (items) => {
    if (this.setBasketItems(items) === false) return false;
    localStorage.setItem("basketStorage", JSON.stringify(items));
    return true;
  };

  removeItem = item => this._sessionStarted && this.snapshotMode === 'enforced'
    ? this._runEnforcedBasketMutation('remove', {item})
    : this._runLegacySessionOperation('remove', context => this._legacyRemoveItem(item, context));

  _legacyRemoveItem = async (item, context) => {
    this.update = true;
    if (this.user.isAuth) {
      await deleteBaseBasketOneItem(item, this.user.user.id);
      if (!this._legacyContinuationCurrent(context)) return false;
      /*if (response) {
                let newBasketItems = []
                response.items.forEach((item) => {
                    let cur = this.basketItems.filter(basketItem => basketItem.item.id === item.item.id)
                    newBasketItems.push({...item, selected: cur.selected})
                })

                //this.setBasket(newBasketItems);
            }*/
    }

    const newBasketItems = this.basketItems.filter((basketItem) => {
      const is =
        basketItem.item.id !== item.item.id ||
        basketItem.currentSize !== item.currentSize ||
        basketItem.colorId !== item.colorId;
      return is;
    });

    this.setBasket(newBasketItems);

    const categoriesResponse = await getCategories(
      item.item.product.product_category
    );
    if (!this._legacyContinuationCurrent(context)) return false;
    if (categoriesResponse.success && item.child_product_size) {
      const category = item.product.product_category
        .map((id) => categoriesResponse.data[id])
        .join("/");
      const findSize = item.child_product_size.find(
        (item) => item.size_name === item.currentSize
      );
      if (findSize) {
        window.dataLayer.push({
          event: "ym-ecom",
          eCategory: "ecommerce",
          eAction: "remove",
          ecommerce: {
            currencyCode: "RUB",
            add: {
              products: [
                {
                  id: String(item.id) + String(item.color.id) + findSize.id,
                  name: item.product.name,
                  price: item.sell_price ? item.sell_price : item.parrent_price,
                  variant: item.color.color_name,
                  category,
                  quantity: 1,
                },
              ],
            },
          },
        });
      }
    }

    this.updateSelected();
  };

  removeAllItems = (operation, sendDataLayer = true) => this._sessionStarted && this.snapshotMode === 'enforced'
    ? this._runEnforcedBasketMutation('clear', {operation})
    : this._runLegacySessionOperation('clear', context => this._legacyRemoveAllItems(operation, sendDataLayer, context));

  _legacyRemoveAllItems = async (operation, sendDataLayer, context) => {
    this.update = true;
    let newBaseBasketItems = [];
    let itemsToDelete = [];
    switch (operation) {
      case "disable": {
        itemsToDelete = this.disabledBasketItems;
        newBaseBasketItems = this.activeBasketItems;
        break;
      }
      case "active": {
        itemsToDelete = this.activeBasketItems;
        newBaseBasketItems = this.disabledBasketItems;
        break;
      }
      case "selected": {
        itemsToDelete = this.selectedItems;
        newBaseBasketItems = this.unSelectedItems;
        break;
      }
      default: {
        itemsToDelete = this.basketItems;
        break;
      }
    }

    if (this.user.isAuth) {
      if (operation === "all") {
        await forceDeleteAllBasketItems(this.user.user.id);
      } else {
        await deleteAllBasketItems(itemsToDelete, this.user.user.id);
      }
      if (!this._legacyContinuationCurrent(context)) return false;
    }
    if (this.basketItems?.length && sendDataLayer) {
      const categoryIds = [];
      this.basketItems.forEach((item) => {
        const { product } = item.item;
        categoryIds.push(...product.product_category);
      });
      const categoriesResponse = await getCategories(categoryIds);
      if (!this._legacyContinuationCurrent(context)) return false;
      let products = [];
      for (let oneProduct of this.basketItems) {
        if (categoriesResponse.success && oneProduct.item.child_product_size) {
          const category = oneProduct.item.product.product_category
            .map((id) => categoriesResponse.data[id])
            .join("/");
          const findSize = oneProduct.item.child_product_size.find(
            (element) => element.size_name === oneProduct.currentSize
          );
          if (category && findSize) {
            products.push({
              id:
                String(oneProduct.item.id) +
                String(oneProduct.item.color.id) +
                findSize.id,
              name: oneProduct.item.product.name,
              price: oneProduct.item.sell_price
                ? oneProduct.item.sell_price
                : oneProduct.item.parrent_price,
              category,
              variant: oneProduct.item.color.color_name,
              quantity: oneProduct.count,
            });
          }
        }
      }
      if (products?.length) {
        window.dataLayer.push({
          event: "ym-ecom",
          eCategory: "ecommerce",
          eAction: "remove",
          ecommerce: {
            currencyCode: "RUB",
            remove: { products },
          },
        });
      }
    }
    this.setBasket(newBaseBasketItems);
    this.updateSelected();
  };

  chenageCountItem = (modifier, item) => this._sessionStarted && this.snapshotMode === 'enforced'
    ? this._runEnforcedBasketMutation('count', {modifier, item})
    : this._runLegacySessionOperation('count', context => this._legacyChangeCount(modifier, item, context));

  _legacyChangeCount = async (modifier, item, context) => {
    this.update = true;
    if (item.count === 1 && modifier === "decrease") {
      return;
    }
    if (this.user.isAuth) {
      const response = await updateCountItemsBasket(
        modifier,
        item,
        this.user.user.id
      );
      if (!this._legacyContinuationCurrent(context)) return false;
      if (!response.items) {
        return;
      }
      //this.setBasket(response.items);
    }

    const index = this.basketItems.findIndex((basketItem) => {
      return (
        basketItem.item.id === item.item.id &&
        item.currentSize === basketItem.currentSize &&
        item.colorId === basketItem.colorId
      );
    });
    if (index >= 0) {
      this.basketItems[index].count += modifier === "increase" ? 1 : -1;
      this.setBasket([...this.basketItems]);
    }

    this._sessionPixel(context, modifier === 'increase' ? 'add' : 'remove', item.item.product.id);

    const method = modifier === "increase" ? "add" : "remove";
    const categoriesResponse = await getCategories(
      item.item.product.product_category
    );
    if (!this._legacyContinuationCurrent(context)) return false;
    if (categoriesResponse.success && item.child_product_size) {
      const category = item.product.product_category
        .map((id) => categoriesResponse.data[id])
        .join("/");
      const findSize = item.child_product_size.find(
        (item) => item.size_name === item.currentSize
      );
      if (findSize) {
        window.dataLayer.push({
          event: "ym-ecom",
          eCategory: "ecommerce",
          eAction: method,
          ecommerce: {
            currencyCode: "RUB",
            [method]: {
              products: [
                {
                  id: String(item.id) + String(item.color.id) + findSize.id,
                  name: item.product.name,
                  price: item.sell_price ? item.sell_price : item.parrent_price,
                  variant: item.color.color_name,
                  category,
                  quantity: 1,
                },
              ],
            },
          },
        });
      }
    }
  };

  upStepPage = (number, onCreated) => this.snapshotMode === 'enforced' ? this._ownedUpStepPage(number) :
    this._runLegacySessionOperation('order', context => this._legacyUpStepPage(number, context, onCreated));

  async _ownedUpStepPage(number) {
    if (![1, 2, 3, 4].includes(number) || !this.ordinaryCheckoutCanAdvance) return false;
    const identity = this._checkoutOperationIdentity();
    const result = await this.enqueueSessionOperation('order', async context => {
      const current = () => this._sessionOperationCurrent(context) && context.snapshotVersion === this.localSnapshotVersion &&
        context.requestSeq === this._snapshotRequestSeq && identity === this._checkoutOperationIdentity() && this.ordinaryCheckoutCanAdvance;
      if (!current()) return false;
      if (number === 4 && (!await this._validatedCheckoutStep(2, current, null, false) ||
        !await this._validatedCheckoutStep(3, current, null, false))) return false;
      return this._validatedCheckoutStep(number, current, () => this._createOrdinaryCheckout(context));
    });
    return result.status === 'accepted' ? result.value : false;
  }

  _legacyUpStepPage = async (number, context, onCreated) => {
    const checkoutIdentity = this._checkoutOperationIdentity();
    const current = () => this._legacyContinuationCurrent(context) && checkoutIdentity === this._checkoutOperationIdentity();
    try {
      const checkBasket = await checkBasketItems(this.selectedItems);
      if (!current()) return false;


      if (!checkBasket.success) {
        console.warn("Basket check failed, reloading page");
        window.location.reload();
        return;
      }
      const result = await this._validatedCheckoutStep(number, current, () => this._legacyCreateOrder(context, current, onCreated));
      return number === 4 || result !== true ? result : undefined;
    } catch (err) {
      if (!current()) return false;
      throw new Error(BASKET_SNAPSHOT_ERROR);
    }
  };

  // Both lanes keep the existing delivery, recipient, SMS and payment checks.
  // Only their authority/creation operations differ.
  _validatedCheckoutStep = async (number, current, create, advance = true) => {
      if (!current()) return false;
      if (this.startBasketNum === 1) {

        if (this.basketOverPrice) {
          console.warn("Basket over price");
          return false;
        }

        if (this.user.askRegistration && !this.user.isAuth) {
          console.warn("User not auth, needs registration");
          this.setRegistrationOnFlyBasket(true);
          this.user.setStepModals("registration");
          this.user.setOpenAuthModal(1);
          this.user.setShadowOverlay(true);
          return false;
        }
      }

      if (number === 2) {

        let stop = false;

        if (!this.delivery.cityInfo) {
          console.warn("No city info");
          if (this.user.isAuth && this.user.user?.name) {
            this.delivery.setCityInfoError(
              this.user.user.name + ", выберите город."
            );
          } else {
            this.delivery.setCityInfoError("Выберите город.");
          }
          stop = true;
        }

        if (!this.delivery.active) {
          console.warn("No active delivery");
          if (this.user.isAuth && this.user.user?.name) {
            this.delivery.setError(
              this.user.user.name + ", выберите способ доставки."
            );
          } else {
            this.delivery.setError("Выберите способ доставки.");
          }
          stop = true;
        }

        if (this.delivery.active?.id === 16 && !this.delivery.officePvz) {
          console.warn("Delivery id=16 but no officePvz");
          this.delivery.setError(
            "Повторное нажатие на способ доставки откроет карту."
          );
          stop = true;
        }

        if (stop) {
          console.warn("Step 2 stop");
          return false;
        }
      }

      if (number === 3) {
        let stop = false;
        const scrollToRecipient = () => {
          const target = document.querySelector(".first_basket_order_user_fields_wrapper") ||
            document.querySelector(".form-recipient");
          if (target) window.scrollTo({top: target.offsetTop, behavior: "smooth"});
        };

        if (!this.delivery.customerInfo.name) {
          this.delivery.setNameValidShield("Укажите Имя");
          stop = true;
          console.warn("Missing name");
          scrollToRecipient();
        }

        if (!this.user.user && this.findUserByNumber) {
          console.warn("User not found by number");
          stop = true;
        }

        if (!this.delivery.customerInfo.telephone) {
          this.delivery.setTelephoneValidShield("Укажите номер телефона");
          console.warn("Missing phone");
          stop = true;
          scrollToRecipient();
        }

        if (this.delivery.customerInfo.telephone && this.countryAndMask) {
          let phoneTest = this.delivery.customerInfo.telephone;
          let newphone = phoneTest.replace(/[^+\d]/g, "").replace("+", "");

          if (newphone?.length !== Number(this.countryAndMask.number)) {
            this.delivery.setTelephoneValidShield("Укажите корректный номер");
            console.warn("Invalid phone length");
            stop = true;
            scrollToRecipient();
          }
        }

        if (this.delivery.customerInfo.telephone < 8 && !this.countryAndMask) {
          this.delivery.setTelephoneValidShield("Укажите настоящий номер");
          console.warn("Invalid phone (too short)");
          stop = true;
          scrollToRecipient();
        }

        let emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (
          !emailPattern.test(this.delivery.customerInfo.email) ||
          !this.delivery.customerInfo.email
        ) {
          this.delivery.setEmailValidShield("Укажите настоящий Email");
          console.warn("Invalid email");
          stop = true;
          scrollToRecipient();
        } else {
          this.delivery.setEmailValidShield("");
        }

        if (
          !this.delivery.officePvz &&
          this.delivery.active?.show_room === false
        ) {
          if (
            !this.orderSearchQuery.value ||
            this.delivery.addressValidShield
          ) {
            console.warn("Invalid address");
            stop = true;
            if (!this.delivery.addressValidShield) {
              this.delivery.setAddressValidShield(
                "Пожалуйста, укажите корректный адрес"
              );
            }
            scrollToRecipient();
          }
        }

        if (
          !this._confirmedUserPhone &&
          !this.user.isAuth &&
          this._countryAndMask
        ) {
          console.warn("Phone not confirmed");
          stop = true;
          this.delivery.setTelephoneValidShield("Пожалуйста подтвердите номер");
          scrollToRecipient();
        }

        if (stop) {
          return;
        }
      }

      if (number === 4) {
        if (!this.selectPaymentType) {
          this.setSelectPaymentTypeError("Выберите способ оплаты");
          return;
        }

        if (this.hasGift && this.selectPaymentType.code === "dolyami") {
          this.setSelectPaymentTypeError(
            "Недоступна покупка сертификатов при оплате Долями"
          );
          return;
        }

        if (
          this.totalPrice < 3000 &&
          (this.selectPaymentType.code === "dolyami" ||
            this.selectPaymentType.code === "ysplit")
        ) {
          let errorText = "Минимальная сумма заказа 3 000р. при оплате";
          errorText +=
            this.selectPaymentType.code === "dolyami"
              ? " Долями"
              : " Яндекс.Сплит";
          this.setSelectPaymentTypeError(errorText);
          return;
        }

        return create();
      }
      if (advance) this.setStartBasketNum(number + 1);
      return true;
  };

  _legacyCreateOrder = async (context, current, onCreated) => {
        let orderData = {
          user_data: this.user.user,
          BasketItems: this.calculatedItems,
          cityInfo: this.delivery.cityInfo,
          delivery_client: this.delivery.active,
          deliveryPrice: this.delPrice,
          orderAdressSearch: this.orderSearchQuery,
          deliveryOrderFields: {
            all: this.delivery.selectedAddress?.addressFields?.all,
            street: this.delivery.selectedAddress?.addressFields?.street,
            house: this.delivery.selectedAddress?.addressFields?.house,
            comment: this.delivery.selectedAddress?.addressFields?.comment,
            flat: this.delivery.selectedAddress?.addressFields?.apartment,
            front_door: this.delivery.selectedAddress?.addressFields?.front_door,
            floor: this.delivery.selectedAddress?.addressFields?.floor,
            intercom: this.delivery.selectedAddress?.addressFields?.intercom,
            name: this.delivery.customerInfo.name,
            surname: this.delivery.customerInfo.surname,
            patronymic: '',
            telephone: this.delivery.customerInfo.telephone,
            email: this.delivery.customerInfo.email,
          },
          pvz_office: this.delivery.officePvz,
          payment_type_json: this.selectPaymentType,
          promocode: this.promocode,
          certificates: this.certificates.filter((item) => item.value && item.isActive)
              .map(({ toggle, loading, ...rest }) => rest),
          activeSubs: this.user.activeSubs,
          avoidCall: this.avoidCall,
          spendBonuses: this.bonusesSpend,
          orderTotal: this.totalPrice,
          orderNoSale: this.priceNoSell,
          orderDelPrice: this.delPrice,
          promoDiscountSumm: this.promoDiscountSumm,
        };

        const response = await createNewOrder(orderData);
        if (!current()) return false;

        if (response?.status === "success") {
          // The successful cleanup unmounts TotalBasket. Hand the current owner's
          // acknowledged order to the router first, not to that removed button.
          if (typeof onCreated === 'function' && response.order?.custom_order_id) onCreated(response);
          await this._legacyRemoveAllItems("all", false, context);
          if (!this._legacyContinuationCurrent(context)) return false;
        }
        this.certificates = [""]
        this.delivery.clear();
        this.init();
        return response;
  };

  downStepPage = (number) => {
    if (!number || number <= 1) {
      return;
    }

    this.setStartBasketNum(number - 1);
  };

  divideBasket(items) {
    void this._snapshotClock;
    let active = [];
    let disabled = [];
    if (!items || !items?.length) {
      return { active: active, disabled: disabled };
    }
    items.forEach((item) => {
      if (this.snapshotLocked || this._expiredBasketLine(item) || !isBasketLineAvailable(item)) {
        disabled.push(item);
      } else {
        active.push(item);
      }
    });
    return { active: active, disabled: disabled };
  }
}
